// src/services/adMob.ts
import { RODDO_ADMOB, canUseNativeAdMob } from '../constants/adMob';

export type RoddoAdMobInitState =
  | 'idle'
  | 'ready'
  | 'consent-blocked'
  | 'disabled'
  | 'failed';

export type RoddoAdPrivacyState = {
  canRequestAds: boolean;
  privacyOptionsRequired: boolean;
  consentStatus: string;
};

let sdkInitPromise: Promise<void> | null = null;
let initPromise: Promise<RoddoAdMobInitState> | null = null;
let initState: RoddoAdMobInitState = 'idle';
let initErrorReason = '';
let privacyState: RoddoAdPrivacyState = {
  canRequestAds: false,
  privacyOptionsRequired: false,
  consentStatus: 'UNKNOWN',
};

export function getRoddoAdMobInitState(): RoddoAdMobInitState {
  return initState;
}

export function getRoddoAdMobInitErrorReason(): string {
  return initErrorReason;
}

export function getRoddoAdPrivacyState(): RoddoAdPrivacyState {
  return { ...privacyState };
}

function applyConsentInfo(info: any): RoddoAdPrivacyState {
  privacyState = {
    canRequestAds: info?.canRequestAds === true,
    privacyOptionsRequired:
      String(info?.privacyOptionsRequirementStatus || '').toUpperCase() === 'REQUIRED',
    consentStatus: String(info?.status || 'UNKNOWN').toUpperCase(),
  };
  return getRoddoAdPrivacyState();
}

function getErrorText(error: unknown): string {
  const value = error as any;
  return String(value?.message || value?.code || value || 'unknown_error');
}

function isAndroidBannerParentInitQuirk(error: unknown): boolean {
  const message = getErrorText(error).toLowerCase();
  return message.includes('admob initialized') && message.includes('banner parent view');
}

async function ensureSdkInitialized(mod: typeof import('@capacitor-community/admob')): Promise<void> {
  if (!sdkInitPromise) {
    sdkInitPromise = (async () => {
      if (import.meta.env.DEV) console.info('[Roddo AdMob] sdk initialize:start');
      try {
        await mod.AdMob.initialize();
        if (import.meta.env.DEV) console.info('[Roddo AdMob] sdk initialize:ready');
      } catch (error) {
        // @capacitor-community/admob Android can initialize MobileAds successfully and
        // then reject while waiting for a banner parent view. Roddo is not requesting
        // a banner here, so that exact post-initialize rejection must not block
        // rewarded/interstitial formats. Do not soften any other initialization error.
        if (isAndroidBannerParentInitQuirk(error)) {
          if (import.meta.env.DEV) {
            console.warn('[Roddo AdMob] sdk initialize:banner-parent quirk ignored', getErrorText(error));
          }
          return;
        }
        throw error;
      }
    })().catch((error) => {
      sdkInitPromise = null;
      throw error;
    });
  }
  return sdkInitPromise;
}

async function resolveConsent(mod: typeof import('@capacitor-community/admob')) {
  if (import.meta.env.DEV) console.info('[Roddo AdMob] ump request:start');
  let info = await mod.AdMob.requestConsentInfo();
  if (import.meta.env.DEV) {
    console.info('[Roddo AdMob] ump request:result', {
      status: String(info?.status || 'UNKNOWN'),
      canRequestAds: info?.canRequestAds === true,
      formAvailable: info?.isConsentFormAvailable === true,
      privacyOptionsRequirementStatus: String(info?.privacyOptionsRequirementStatus || 'UNKNOWN'),
    });
  }

  if (
    info?.isConsentFormAvailable === true &&
    String(info?.status || '').toUpperCase() === 'REQUIRED'
  ) {
    if (import.meta.env.DEV) console.info('[Roddo AdMob] ump form:show');
    info = await mod.AdMob.showConsentForm();
    if (import.meta.env.DEV) console.info('[Roddo AdMob] ump form:closed', {
      status: String(info?.status || 'UNKNOWN'),
      canRequestAds: info?.canRequestAds === true,
    });
  }

  return applyConsentInfo(info);
}

export async function initializeRoddoAdMob(): Promise<RoddoAdMobInitState> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (!RODDO_ADMOB.enabled || !canUseNativeAdMob()) {
      initState = 'disabled';
      initErrorReason = '';
      return initState;
    }

    try {
      const mod = await import('@capacitor-community/admob');

      // Capacitor Community AdMob v8 recommends SDK initialization first,
      // then a fresh UMP consent-info request before any ad is requested.
      await ensureSdkInitialized(mod);
      const consent = await resolveConsent(mod);

      initState = consent.canRequestAds ? 'ready' : 'consent-blocked';
      initErrorReason = '';
      if (import.meta.env.DEV) console.info('[Roddo AdMob] initialize:state', initState, consent);
      return initState;
    } catch (error) {
      initState = 'failed';
      initErrorReason = getErrorText(error).slice(0, 160);
      privacyState = {
        canRequestAds: false,
        privacyOptionsRequired: false,
        consentStatus: 'UNKNOWN',
      };
      if (import.meta.env.DEV) {
        console.warn('[Roddo AdMob] initialization/consent failed', getErrorText(error), error);
      }
      return initState;
    }
  })();

  return initPromise;
}

export async function refreshRoddoAdPrivacy(): Promise<RoddoAdPrivacyState> {
  if (!RODDO_ADMOB.enabled || !canUseNativeAdMob()) {
    return getRoddoAdPrivacyState();
  }

  try {
    const mod = await import('@capacitor-community/admob');
    await ensureSdkInitialized(mod);
    return await resolveConsent(mod);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Roddo AdMob] privacy refresh failed', error);
    }
    return getRoddoAdPrivacyState();
  }
}

export async function showRoddoAdPrivacyOptions(): Promise<boolean> {
  if (!RODDO_ADMOB.enabled || !canUseNativeAdMob()) return false;

  try {
    const mod = await import('@capacitor-community/admob');
    await mod.AdMob.showPrivacyOptionsForm();
    // Refresh UMP state after the user closes the privacy-options form.
    await refreshRoddoAdPrivacy();
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Roddo AdMob] privacy options unavailable', error);
    }
    return false;
  }
}
